<script>
import toastr from "toastr";
import { mapState } from "vuex";

export default {
  name: "NavLayout",
  computed: {
    ...mapState("property", ["template"]),
    property() { return this.$store.state.property.property; },
    user() { return this.$store.state.userRoot; },
    step() { return this.$store.state.step || 1; },

    normalizedType() {
      return (this.template?.type || "").toString().toLowerCase();
    },
    isInitialCreateFlow() {
      return this.$route.name === "firstCreatePropiedad";
    },
    isHotelFlow() {
      return this.normalizedType === "hotel" || this.normalizedType === "camping";
    },
    isAddingToListing() {
      return !!(this.template?.attach_listing_id) || !!this.$route.query.attach_listing_id;
    },
    // Título contextual del nav: "Añadir unidad" al adjuntar, si no el de crear.
    navTitle() {
      if (this.isInitialCreateFlow && this.isAddingToListing) return "Añadir unidad";
      return this.$t("property.createProperty.title");
    },

    maxSteps() {
      if (this.isInitialCreateFlow) {
        // El total lo controla el asistente nuevo (firstCreatePropiedad.vue).
        return Math.max(1, Number(this.$store.state.wizardTotal) || 1);
      }

      return this.isHotelFlow ? 8 : 7;
    },

    // progreso: porcentaje según cantidad real de pasos
    progressWidth() {
      const current = Math.min(this.step, this.maxSteps);
      return `${(100 / this.maxSteps) * current}%`;
    },
  },
  methods: {
    logout() {
      this.$store.dispatch("destroyToken")
        .then(() => { window.location.href = "/"; })
        .catch(() => { toastr.error(this.$t("app.t.text79")); });
    },
    alertLogout() {
      this.$swal({
        title: this.$t("app.t.text80"),
        text: this.$t("app.t.text81"),
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "gray",
        confirmButtonColor: "#c00202",
        confirmButtonText: this.$t("app.t.text82"),
        cancelButtonText: this.$t("app.t.text83"),
      }).then((r) => {
        if (r.isConfirmed) {
          this.$store.state.loader = true;
          this.logout();
        }
      });
    },
    exitPage() {
      // Si se abrió desde el gestor de anuncios, volvemos allí.
      if (this.$route.query.from === "listings") {
        this.$router.push({ name: "ListingManager" });
        return;
      }
      // Si el usuario YA tiene alojamientos, salimos a la vista de anuncios.
      const hasProperties =
        !!this.$store.state.propiedadActiva ||
        !!this.user?.properties?.[0] ||
        !!this.property;
      if (hasProperties) {
        this.$router.push({ name: "ListingManager" });
        return;
      }
      // No tiene ninguno (onboarding tras registro): preguntar si cerrar sesión o continuar.
      this.alertLogout();
    },
    prevPage() {
      if (this.step > 1) this.$store.state.step -= 1;
    },
  },
};
</script>

<template>
  <div class="nav-layouts">
    <div
      v-if="step > 1 && !isInitialCreateFlow"
      @click.prevent="prevPage()"
      class="nav-back"
    >
      <div class="nav-back__icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M7.49 12 3.74 8.248m0 0 3.75-3.75m-3.75 3.75h16.5V19.5"/>
        </svg>
      </div>
      <div class="nav-back__label">{{ $t("hcExtra.nav.volver") }}</div>
    </div>

    <div class="title-nav only-lg">
      {{ navTitle }}
      <span v-if="maxSteps > 1" class="step-number">
        {{ step }} {{ $t("property.createProperty.of") }} {{ maxSteps }}
      </span>
    </div>

    <div class="buttons-nav d-flex flex-row gap-4">
      <div class="svg-icon">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
        </svg>
      </div>
      <div class="line"></div>
      <div class="svg-icon">
        <svg @click.prevent="exitPage()" style="cursor:pointer" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
        </svg>
      </div>
    </div>

    <div class="progress">
      <div :style="{ width: progressWidth }" class="progress-bar" role="progressbar"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap");

@media (max-width: 768px) {
  .nav-layouts {
    justify-content: center;
    padding-inline: 16px;
  }
}

.nav-layouts {
  height: 60px;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 45px 3px #eee;
  z-index: 1000;
}
.title-nav {
  color: #0f172a;
  font-size: 15px;
  font-weight: 600;
  font-family: "DM Sans", system-ui, sans-serif;
  letter-spacing: -0.01em;
  span {
    font-weight: 400;
    color: #64748b;
  }
}
.nav-back {
  position: absolute;
  left: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 14px;
  background: #fff;
  border: 1px solid #e5ebf3;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(148, 163, 184, 0.08);
  cursor: pointer;
  color: #3b4961;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.nav-back:hover {
  border-color: #cbd5e1;
  box-shadow: 0 14px 30px rgba(148, 163, 184, 0.12);
  transform: translateY(-1px);
}

.nav-back__icon {
  width: 18px;
  display: flex;
  align-items: center;
}

.nav-back__label {
  font-weight: 600;
  font-size: 14px;
}

.progress {
  width: 100%;
  position: absolute;
  border-radius: 0;
  bottom: -19px;
  z-index: 0;
  height: 4px;
  box-shadow: none !important;
  background-color: #ede9fe; /* morado muy claro como track */
}

.progress-bar {
  background-color: #673de6 !important; /* morado principal */
  border-radius: 0;
  transition: width 0.4s ease;
}
.buttons-nav {
  position: absolute;
  right: 2%;
  justify-content: space-between;
  width: 100px;
  align-items: center;
}

.svg-icon {
  width: 28px;
}

.line {
  width: 1px;
  background: #e2e2e2;
  height: 38px;
}

@media (max-width: 768px) {
  .nav-back {
    left: 12px;
    padding: 0 10px;
    min-height: 38px;
  }

  .nav-back__label {
    display: none;
  }

  .buttons-nav {
    right: 12px;
    width: 92px;
  }
}

/* ===== RTL (arabe): volver a la derecha, acciones a la izquierda ===== */
[dir="rtl"] {
  .nav-back { left: auto; right: 16px; }
  .buttons-nav { right: auto; left: 2%; }
}
@media (max-width: 768px) {
  [dir="rtl"] {
    .nav-back { left: auto; right: 12px; }
    .buttons-nav { right: auto; left: 12px; }
  }
}
</style>
